import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Typography,
  Box,
  Stack,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Key as KeyIcon,
  Lock as LockIcon,
} from "@mui/icons-material";

function ChangePasswordModal({ onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Original state variables (unchanged)
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const primaryColor = "#0d6efd";

  // Original event handler (enhanced with validation)
  const handlePasswordChange = async () => {
    setError('');

    // Basic validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New password and confirm password do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    try {
      // Simulate API call - replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      console.log('Password changed!');
      onClose();
    } catch (error) {
      setError('Failed to change password. Please try again.');
    }
    setLoading(false);
  };

  // Original toggle visibility logic (unchanged)
  const toggleVisibility = (field) => {
    switch (field) {
      case 'current':
        setShowCurrentPassword(!showCurrentPassword);
        break;
      case 'new':
        setShowNewPassword(!showNewPassword);
        break;
      case 'confirm':
        setShowConfirmPassword(!showConfirmPassword);
        break;
      default:
        break;
    }
  };

  // Check if form is valid
  const isFormValid = currentPassword && newPassword && confirmPassword && 
                     newPassword === confirmPassword && newPassword.length >= 6;

  return (
   <Dialog
  open={true}
  onClose={onClose}
  maxWidth="sm"
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: 2,
      overflow: 'hidden',
    }
  }}
>
  {/* Header */}
  <DialogTitle
    sx={{
      bgcolor: '#FFFFFF',
      color: 'black',
      p: 3,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <KeyIcon sx={{ fontSize: 28, color: 'black' }} />
      <Typography variant="h6" sx={{ fontWeight: 600, color: 'black' }}>
        Change Your Password
      </Typography>
    </Box>
    
    <IconButton
      onClick={onClose}
      sx={{
        color: 'black', // Changed from white to black
        '&:hover': {
          backgroundColor: 'rgba(0,0,0,0.1)', // Changed hover color
        }
      }}
      size="small"
    >
      <CloseIcon />
    </IconButton>
  </DialogTitle>

  {/* Content */}
  <DialogContent sx={{ p: 3 }}>
    <Stack spacing={3} sx={{ mt: 1 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Current Password */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, color: 'black', fontWeight: 600 }}>
          Current Password
        </Typography>
        <TextField
          fullWidth
          type={showCurrentPassword ? 'text' : 'password'}
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          placeholder="Enter your current password"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon sx={{ color: 'black', fontSize: 20 }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => toggleVisibility('current')}
                  edge="end"
                  size="small"
                >
                  {showCurrentPassword ? 
                    <VisibilityOffIcon fontSize="small" sx={{ color: 'black' }} /> : 
                    <VisibilityIcon fontSize="small" sx={{ color: 'black' }} />
                  }
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'rgba(0,0,0,0.23)',
              },
              '&:hover fieldset': {
                borderColor: 'black',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'black',
              },
            },
          }}
        />
      </Box>

      {/* New Password */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, color: 'black', fontWeight: 600 }}>
          New Password
        </Typography>
        <TextField
          fullWidth
          type={showNewPassword ? 'text' : 'password'}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="Enter your new password"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon sx={{ color: 'black', fontSize: 20 }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => toggleVisibility('new')}
                  edge="end"
                  size="small"
                >
                  {showNewPassword ? 
                    <VisibilityOffIcon fontSize="small" sx={{ color: 'black' }} /> : 
                    <VisibilityIcon fontSize="small" sx={{ color: 'black' }} />
                  }
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'rgba(0,0,0,0.23)',
              },
              '&:hover fieldset': {
                borderColor: 'black',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'black',
              },
            },
          }}
        />
        {newPassword && newPassword.length < 6 && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
            Password must be at least 6 characters long
          </Typography>
        )}
      </Box>

      {/* Confirm Password */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, color: 'black', fontWeight: 600 }}>
          Confirm New Password
        </Typography>
        <TextField
          fullWidth
          type={showConfirmPassword ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirm your new password"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon sx={{ color: 'black', fontSize: 20 }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => toggleVisibility('confirm')}
                  edge="end"
                  size="small"
                >
                  {showConfirmPassword ? 
                    <VisibilityOffIcon fontSize="small" sx={{ color: 'black' }} /> : 
                    <VisibilityIcon fontSize="small" sx={{ color: 'black' }} />
                  }
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'rgba(0,0,0,0.23)',
              },
              '&:hover fieldset': {
                borderColor: 'black',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'black',
              },
            },
          }}
        />
        {confirmPassword && newPassword !== confirmPassword && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
            Passwords do not match
          </Typography>
        )}
      </Box>

      {/* Password Requirements */}
      <Box sx={{ bgcolor: 'rgba(0,0,0,0.05)', p: 2, borderRadius: 1 }}>
        <Typography variant="subtitle2" sx={{ color: 'black', fontWeight: 600, mb: 1 }}>
          Password Requirements:
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • At least 6 characters long<br />
          • Should contain a mix of letters and numbers<br />
          • Must not match your current password
        </Typography>
      </Box>
    </Stack>
  </DialogContent>

  {/* Footer */}
  <DialogActions sx={{ p: 3, bgcolor: 'rgba(0,0,0,0.02)' }}>
    <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
      <Button
        onClick={onClose}
        variant="outlined"
        sx={{
          borderColor: 'black',
          color: 'black',
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.1)',
            borderColor: 'black',
          },
          flex: 1,
        }}
      >
        Cancel
      </Button>
      
      <Button
        onClick={handlePasswordChange}
        variant="contained"
        disabled={!isFormValid || loading}
        sx={{
          backgroundColor: 'black',
          '&:hover': {
            backgroundColor: 'black',
            opacity: 0.9,
          },
          flex: 1,
        }}
      >
        {loading ? 'Changing...' : 'Set Password'}
      </Button>
    </Stack>
  </DialogActions>
</Dialog>
  );
}

export default ChangePasswordModal;